import React, { useRef, useState } from "react";
import {
  Card,
  Box,
  Typography,
  IconButton,
  Paper,
  Button,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ChecklistIcon from "@mui/icons-material/Checklist";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useTranslation } from "react-i18next";
import Matrix6 from "../../../img/logos/Matrix6.png";
import GarmentProdCheckListDocument from "./GarmentProdCheckListDocument";
import { CHECKLIST_ACCENT } from "./checklistStyles";

const GarmentProdCheckListCard = () => {
  const [open, setOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const contentRef = useRef(null);

  const handleToggle = () => setOpen((prev) => !prev);

  const handleDownloadPDF = async () => {
    const root = contentRef.current;
    if (!root) return;

    const pages = root.querySelectorAll(".checklist-page");
    if (!pages.length) return;

    const doc = new jsPDF({
      unit: "mm",
      format: "a4",
      orientation: "portrait",
    });

    for (let i = 0; i < pages.length; i += 1) {
      const canvas = await html2canvas(pages[i], {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });
      const imgData = canvas.toDataURL("image/png");
      if (i > 0) doc.addPage();
      doc.addImage(imgData, "PNG", 0, 0, 210, 297);
    }

    doc.save(t("garmentProdChecklist.pdfFileName"));
  };

  const closedCardSx = {
    p: 3,
    height: "9em",
    borderRadius: 3,
    cursor: "pointer",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    border: `1px solid ${alpha(CHECKLIST_ACCENT, 0.12)}`,
    background: `linear-gradient(135deg, ${alpha(CHECKLIST_ACCENT, 0.06)} 0%, ${alpha(
      theme.palette.common.white,
      0.98,
    )} 100%)`,
    boxShadow: `0 8px 22px ${alpha(theme.palette.common.black, 0.07)}`,
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: `0 14px 32px ${alpha(CHECKLIST_ACCENT, 0.14)}`,
    },
  };

  return (
    <Box sx={{ position: "relative" }}>
      {!open && (
        <Card onClick={handleToggle} elevation={0} sx={closedCardSx}>
          <Box
            component="img"
            src={Matrix6}
            alt="Matrix Logo"
            sx={{ width: 40, height: 40, mr: 1 }}
          />
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" }, lineHeight: 1.3 }}
          >
            {t("garmentProdChecklist.title")}
          </Typography>
          <ChecklistIcon sx={{ color: CHECKLIST_ACCENT, ml: 1 }} />
        </Card>
      )}

      {open && (
        <Card
          elevation={0}
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95vw", sm: "90vw", md: "88vw" },
            maxHeight: "92vh",
            overflowY: "auto",
            zIndex: 2000,
            borderRadius: 4,
            border: `1px solid ${alpha(CHECKLIST_ACCENT, 0.1)}`,
            boxShadow: `0 24px 64px ${alpha(theme.palette.common.black, 0.22)}`,
            background: "#eef2f7",
            p: 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              px: { xs: 2, md: 3 },
              py: 1.75,
              background: `linear-gradient(135deg, ${alpha(CHECKLIST_ACCENT, 0.1)} 0%, ${alpha(
                theme.palette.common.white,
                0.94,
              )} 72%)`,
              borderBottom: `1px solid ${alpha(CHECKLIST_ACCENT, 0.1)}`,
              backdropFilter: "blur(14px)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                minWidth: 0,
              }}
            >
              <Box
                component="img"
                src={Matrix6}
                alt="Matrix Logo"
                sx={{ width: 42, height: 42, flexShrink: 0 }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="overline"
                  sx={{
                    display: "block",
                    fontWeight: 800,
                    letterSpacing: 0.8,
                    lineHeight: 1,
                    color: alpha(CHECKLIST_ACCENT, 0.7),
                    fontSize: "0.62rem",
                  }}
                >
                  NML · Conocimiento
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 900,
                    lineHeight: 1.2,
                    fontSize: { xs: "0.95rem", md: "1.05rem" },
                  }}
                >
                  {t("garmentProdChecklist.title")}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
              }}
            >
              <Button
                variant="contained"
                size="small"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleDownloadPDF}
                sx={{
                  fontWeight: 800,
                  borderRadius: 999,
                  px: 2,
                  background: `linear-gradient(135deg, ${CHECKLIST_ACCENT} 0%, ${alpha(
                    CHECKLIST_ACCENT,
                    0.85,
                  )} 100%)`,
                  boxShadow: `0 8px 20px ${alpha(CHECKLIST_ACCENT, 0.28)}`,
                }}
              >
                {t("garmentProdChecklist.downloadPDF")}
              </Button>
              <IconButton
                onClick={handleToggle}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.grey[500], 0.1),
                  "&:hover": { bgcolor: alpha(theme.palette.grey[500], 0.18) },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Paper>

          <Box sx={{ py: { xs: 1, md: 2 }, px: { xs: 0.5, md: 1 } }} key={i18n.language}>
            <GarmentProdCheckListDocument ref={contentRef} />
          </Box>
        </Card>
      )}
    </Box>
  );
};

export default GarmentProdCheckListCard;
